import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Box, Button } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { getAllElectronicInvoiceAddresses } from 'src/shared/services/electronicInvoiceAddressService';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { ElectronicInvoiceAddress } from '../models/electronicInvoiceAddress';
import { EInvoiceAddressSearchFilter } from '../models/eInvoiceAddressSearchFilter';

type EInvoiceAddressDataGridProps = {
  searchFilter: EInvoiceAddressSearchFilter;
  searchFilterCallback: (filter: EInvoiceAddressSearchFilter) => void;
  isOther?: boolean;
};

export const EInvoiceAddressDataGrid = (props: EInvoiceAddressDataGridProps) => {
  const { searchFilter, searchFilterCallback, isOther } = props;
  const { t } = useTranslation(['invoice']);
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const isMounted = useIsMounted();

  const [rows, setRows] = useState<ElectronicInvoiceAddress[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }

    try {
      const result = await getAllElectronicInvoiceAddresses(searchFilter, false, isOther);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(
            result.data.results.map((item) => {
              return { ...item, id: item.eInvoiceAddress_id };
            }),
          );
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [isMounted, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const columns: GridColDef[] = [
    {
      field: 'eInvoiceAddress_id',
      headerName: 'ID',
      sortable: true,
      flex: 1,
    },
    {
      field: 'firstName',
      headerName: t('invoice:eInvoice.address.tableHeader.firstName'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.member?.firstName}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('invoice:eInvoice.address.tableHeader.lastName'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.member?.lastName}</>;
      },
    },
    {
      field: 'memberNr',
      headerName: isOther ? t('invoice:eInvoice.address.tableHeader.nr') : t('invoice:eInvoice.address.tableHeader.memberNr'),
      sortable: true,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.member?.memberNr || '–'}</>;
      },
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        const memberId = params.row.member_id;
        const memberIsOther = params.row.member?.other === true;
        const route = memberIsOther ? RoutesEnum.MemberOtherContactDetails : RoutesEnum.MemberDetails;
        return (
          <Button
            variant='outlined'
            size='small'
            endIcon={<OpenInNew sx={{ fontSize: '0.85rem !important' }} />}
            onClick={() => window.open(`${route}/${memberId}`, '_blank')}
          >
            {t('common:button.open')}
          </Button>
        );
      },
    },
    {
      field: 'receiverName',
      headerName: t('invoice:eInvoice.address.tableHeader.receiverName'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.receiver?.name}</>;
      },
    },
    {
      field: 'accountingCost',
      headerName: t('invoice:eInvoice.address.tableHeader.accountingCost'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.accountingCost}</>;
      },
    },
    {
      field: 'buyerReference',
      headerName: t('invoice:eInvoice.address.tableHeader.reference'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.buyerReference}</>;
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
          py: 1,
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<ElectronicInvoiceAddress>
        columns={columns}
        rows={rows}
        autoHeight
        dataModel={data}
        columnVisibilityModel={{ eInvoiceAddress_id: false }}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort }]);
          }
          searchFilterCallback({
            ...searchFilter,
            pageIndex: 0,
            sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
            sortDir: newSortModel.length ? newSortModel[0].sort : sort,
          });
        }}
        selectionModel={[]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
